// @flow

import type {
  ActionHandlers,
  Reducer,
  Selector,
} from '@travel-frontend/state-handling-sdk';
import type {
  FetchCustomerError,
  FetchCustomerSuccess,
} from 'redux/v1/customer/data/actions';
import type {DataState} from 'redux/v1/customer/data/DataState';
import type {Customer} from 'redux/v1/customer/data/models';
import type {PutCustomerSuccess} from 'redux/v1/customer/put/actions';

import {
  createReducerUsingActionHandlers,
  ERROR,
  PENDING,
  SUCCESS,
} from '@travel-frontend/state-handling-sdk';
import {createSelector} from 'reselect';

import {dataState} from 'redux/v1/customer/CustomerState';
import {FETCH_CUSTOMER} from 'redux/v1/customer/data/actions';
import {DELETE_CUSTOMER} from 'redux/v1/customer/delete/actions';
import {PUT_CUSTOMER} from 'redux/v1/customer/put/actions';
import {formatISOtoDateString} from 'utils';

export const initialState: DataState = {
  loading: false,
};

const actionCreators: ActionHandlers<DataState> = {
  [PENDING(FETCH_CUSTOMER)]: (state: DataState) => ({
    ...state,
    loading: true,
  }),
  [SUCCESS(FETCH_CUSTOMER)]: (
    state: DataState,
    action: FetchCustomerSuccess
  ) => ({
    ...state,
    loading: false,
    data: {
      ...action.data,
      ...(action.data?.birthday
        ? {birthday: formatISOtoDateString(action.data?.birthday)}
        : {}),
    },
  }),
  [ERROR(FETCH_CUSTOMER)]: (state: DataState, action: FetchCustomerError) => {
    // TODO: must be refactored after -> FBO-1758 (v6 responses refactoring)
    // "2097479684" v6 error code -> "No customer for journey ..."
    if (action.error.code === 2_097_479_684) {
      return {
        ...state,
        loading: false,
        data: {},
        errors: action.error,
      };
    }

    return {
      ...state,
      loading: false,
      errors: action.error,
    };
  },
  [SUCCESS(PUT_CUSTOMER)]: (state: DataState, action: PutCustomerSuccess) => ({
    ...state,
    loading: false,
    data: action.customer,
  }),
  [SUCCESS(DELETE_CUSTOMER)]: (
    state: DataState,
    action: PutCustomerSuccess
  ) => ({
    ...state,
    data: null,
  }),
};

export const dataReducer: Reducer<DataState> = // $FlowIgnore
  createReducerUsingActionHandlers<DataState>(initialState, actionCreators);

export const isDataLoaded: Selector<boolean> = createSelector(
  dataState,
  (state: DataState) => !state.loading && state.data !== undefined
);

export const isDataEmpty: Selector<boolean> = createSelector(
  [dataState, isDataLoaded],
  (state: DataState, loaded: boolean) =>
    !loaded || !state.data || Object.keys(state.data).length === 0
);

export const getData: Selector<Customer> = createSelector(
  [dataState, isDataLoaded],
  (state: DataState, loaded: boolean) =>
    loaded && state.data ? state.data : {}
);

export const getCustomerEmail: Selector<string> = createSelector(
  [dataState, isDataLoaded],
  (state: DataState, loaded: boolean) =>
    loaded && state.data ? state.data.email : ''
);

export const getCustomerZipCode: Selector<string> = createSelector(
  [dataState, isDataLoaded],
  (state: DataState, loaded: boolean) =>
    loaded && state.data ? state.data.zipCode : ''
);
